import React from "react";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { useHistory } from "react-router-dom";
import _ from "lodash";

import { Text } from "src/library/components/Text";
import { Button } from "src/library/components/Button";
import { DateInput } from "src/core/shared/components/Date/DateInput";
import {
  CheckboxStyled,
  Radio
} from "src/library/components/Forms";
import { SelectOrderTypes } from "src/core/orders/components/SelectOrderTypes";
import {
  CustomerBranchFilters,
  CustomerBranchFiltersModal
} from "src/library/components/CustomerBranchFilter";
import {
  ICustomer,
  ListCustomersParams,
  useCustomerApi
} from "src/core/customers/api/customers.api";
import useTerminology from "src/core/shared/hooks/useTerminology";
import { CustomerBranchFilterAction } from "src/library/components/CustomerBranchFilter/CustomerBranchFilters";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { Tooltip } from "src/library/components/Tooltip";
import { Icons } from "src/library/components/Icon/types";
import { Icon } from "src/library/components/Icon";
import { useSearchApi } from "src/core/shared/api/search.api";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import {
  IDrop,
  ListDropsParams,
  useDropsApi
} from "src/core/drops/api/drops.api";

import {
  CreateProductReplacementParams,
  CustomerBranchFilterDefault,
  CustomerBranchFilterType,
  IProduct,
  useProductsApi,
  ProductReplacement,
  ProductReplacementId,
  ListProductsParams,
  GlobalProductReplacementFilter
} from "../../api/products.api";
import { createProductReplacementSchema } from "./CreateProductReplacementSchema";
import PlannedProductReplacement from "./PlannedProductReplacement";
import ProductReplacementSearch from "./ProductReplacementSearch";

interface FormValidation { error: boolean; message: string }
interface ProductReplacementFormProps {
  duplicateProductReplacement?: CreateProductReplacementParams;
}

const ProductReplacementForm: React.FunctionComponent<ProductReplacementFormProps> = props => {
  const terminology = useTerminology();
  const history = useHistory();
  const [ createProductReplacementRes, createProductReplacementReq ] = useProductsApi("CREATE_PRODUCT_REPLACEMENT");
  const [ productResponse, productSearch ] = useSearchApi("SEARCH_PRODUCTS");
  const [ customersResponse, customerSearch ] = useCustomerApi("LIST_CUSTOMERS");
  const [ dropsResponse, dropSearch ] = useDropsApi("LIST_DROPS");
  const [ showFiltersModal, setShowFiltersModal ] = React.useState(false);
  const [ selectedCustomer, setSelectedCustomer ] = React.useState<ICustomer | null>(null);
  const [ replacements, setReplacements ] = React.useState<ProductReplacement[]>([]);
  const [ duplicateFilters, setDuplicateFilters ] = React.useState<GlobalProductReplacementFilter[]>([]);
  const [ newReplacementRow, setNewReplacementRow ] = React.useState(true);

  const [ validation, setValidation ] = React.useState<FormValidation>({
    error: false,
    message: ""
  });

  const [ form, setForm ] = React.useState<Partial<CreateProductReplacementParams>>({
    type: props.duplicateProductReplacement?.type ?? CustomerBranchFilterDefault.AllowAll,
    filters: [],
    orderTypes: [
      "scheduled",
      "bulk",
      "adhoc"
    ],
    effectOrders: { startDate: moment.utc().toDate() },
    effectLists: false,
    replacements: []
  });

  const getProducts = React.useCallback((productIds: number[]) => {
    const listProductsParams: ListProductsParams = {
      pagination: {
        offset: 0,
        limit: 1000,
        page: 0,
        sort: []
      },
      filters: { id: { $in: productIds } }
    };

    productSearch(listProductsParams);
  }, [ productSearch ]);

  const getCustomers = React.useCallback((customertIds: number[]) => {
    const listCustomersParams: ListCustomersParams = {
      pagination: {
        limit: 1000,
        offset: 0,
        page: 0,
        sort: []
      },
      filters: { id: { $in: customertIds } }
    };

    customerSearch(listCustomersParams);
  }, [ customerSearch ]);

  const getDrops = React.useCallback((dropIds: number[]) => {
    const listDropsParams: ListDropsParams = {
      pagination: {
        limit: 1000,
        offset: 0,
        page: 0,
        sort: []
      },
      filters: { id: { $in: dropIds } }
    };

    dropSearch(listDropsParams);
  }, [ dropSearch ]);

  // ProductIds to search for duplicate product replacements
  React.useEffect(() => {
    const productIdstoSearch = [];

    if (props.duplicateProductReplacement?.replacements?.length) {
      for (const replacement of props.duplicateProductReplacement.replacements) {
        productIdstoSearch.push(replacement.fromProductId);
        productIdstoSearch.push(replacement.toProductId);
      }
    }

    // Only make API call if there are product IDs to search for
    if (productIdstoSearch.length > 0) {
      getProducts(productIdstoSearch);
    }
  }, [ props.duplicateProductReplacement?.replacements, getProducts ]);

  // CustomerIds to search for duplicate product replacement filters if there are any
  React.useEffect(() => {
    const customerIdstoSearch: number[] = [];

    if (props.duplicateProductReplacement?.filters?.length) {
      for (const filter of props.duplicateProductReplacement.filters) {
        if (filter.customerId) {
          customerIdstoSearch.push(filter?.customerId);
        }
      }
    }

    // Only make API call if there are customer IDs to search for
    if (customerIdstoSearch.length > 0) {
      getCustomers(customerIdstoSearch);
    }
  }, [ props.duplicateProductReplacement?.filters, getCustomers ]);

  // BranchIds to get branches to add to duplicate product replacement filter
  React.useEffect(() => {
    const dropIdstoSearch: number[] = [];

    if (props.duplicateProductReplacement?.filters?.length) {
      for (const filter of props.duplicateProductReplacement.filters) {
        if (filter.branchId) {
          dropIdstoSearch.push(filter?.branchId);
        }
      }
    }

    // Only make API call if there are drop IDs to search for
    if (dropIdstoSearch.length > 0) {
      getDrops(dropIdstoSearch);
    }
  }, [ props.duplicateProductReplacement?.filters, getDrops ]);

  // search through products for duplicate product replacements, set the replacements to populate form
  const setDuplicateProductReplacements = React.useCallback(() => {
    const duplicateReplacements = props.duplicateProductReplacement?.replacements;
    const replacementObjectsArr: ProductReplacement[] = [];
    const products = productResponse.data?.result;

    if (duplicateReplacements?.length && products?.length) {
      for (const rep of duplicateReplacements) {
        const fromProductObj = products.find(prod => prod.id === rep.fromProductId);
        const toProductObj = products.find(prod => prod.id === rep.toProductId);

        replacementObjectsArr.push({
          fromProduct: fromProductObj,
          toProduct: toProductObj
        });
      }
    }

    setReplacements(replacementObjectsArr);
    props.duplicateProductReplacement ? setNewReplacementRow(false) : setNewReplacementRow(true);
  }, [ productResponse.data?.result, props.duplicateProductReplacement ]);

  // search through duplicate product replacement filters and add customer/branch to filter object, to set the filters for the customerBranchFilter on page load
  const duplicateFiltersArray = React.useCallback(() => {
    const duplicateReplacementsFilters = props.duplicateProductReplacement?.filters;
    const filterObjectsArr = [];
    const customers = customersResponse.data?.result;
    const drops = dropsResponse.data?.result;
    const products = productResponse.data?.result;

    if (duplicateReplacementsFilters?.length && customers?.length) {
      for (const filter of duplicateReplacementsFilters) {
        if (filter.customerId) {
          const customerObj: ICustomer | undefined = customers?.find(cust => cust?.id === filter.customerId);

          const newFilter = {
            ...filter,
            customer: customerObj
          };

          filterObjectsArr.push(newFilter);
        }
      }
    }

    if (duplicateReplacementsFilters?.length && drops?.length) {
      for (const filter of duplicateReplacementsFilters) {
        if (filter.branchId) {
          const dropObj: IDrop | undefined = drops?.find(drop => drop?.id === filter.branchId);

          const newFilter = {
            ...filter,
            branch: dropObj
          };

          filterObjectsArr.push(newFilter);
        }
      }
    }

    setDuplicateFilters(filterObjectsArr);

    if (products?.length) {
      setDuplicateProductReplacements();
    }
  }, [
    customersResponse.data?.result,
    dropsResponse.data?.result,
    productResponse.data?.result,
    props.duplicateProductReplacement,
    setDuplicateProductReplacements
  ]);

  // call functions to set the duplicates on page load if there are any
  React.useEffect(() => {
    // Only proceed if we have a duplicateProductReplacement prop
    if (!props.duplicateProductReplacement) {
      return;
    }

    const hasReplacements = props.duplicateProductReplacement.replacements?.length > 0;
    const hasFilters = props.duplicateProductReplacement.filters?.length > 0;

    // If no replacements or filters, call duplicateFiltersArray immediately
    if (!hasReplacements && !hasFilters) {
      duplicateFiltersArray();

      return;
    }

    // Check if required API responses are ready based on what data we need
    const needsProducts = hasReplacements;
    const needsCustomers = hasFilters && props.duplicateProductReplacement.filters?.some(f => f.customerId);
    const needsDrops = hasFilters && props.duplicateProductReplacement.filters?.some(f => f.branchId);

    const dependencies = [
      needsProducts ? productResponse.data?.result : true,
      needsCustomers ? customersResponse.data?.result : true,
      needsDrops ? dropsResponse.data?.result : true
    ];

    if (dependencies.every(Boolean)) {
      duplicateFiltersArray();
    }
  }, [
    customersResponse.data?.result,
    dropsResponse.data?.result,
    duplicateFiltersArray,
    productResponse.data?.result,
    props.duplicateProductReplacement
  ]);

  // handles the form events to set the values
  const handleFormChange = React.useCallback(
    (name: keyof CreateProductReplacementParams, value: unknown, callback?: (f: typeof form) => void) => {
      setValidation({
        error: false,
        message: ""
      });

      const newForm = {
        ...form,
        [ name ]: value
      };

      if (!newForm.orderTypes?.includes("scheduled") && newForm.effectLists === true) {
        newForm.effectLists = false;
      }

      setForm(newForm);

      if (callback) {
        callback(newForm);
      }
    },
    [ form ]
  );

  const handleProductFormChange = React.useCallback((index: number, input: keyof ProductReplacement, product?: IProduct) => {
    setValidation({
      error: false,
      message: ""
    });
    //check the from product doesn't already exist in the replacements.
    const fromExists = replacements.filter(rep => rep.fromProduct?.id === product?.id);
    //if the from product is already in the replacements as a to product
    const toExistsAsFrom = replacements.filter(rep => (input === "fromProduct" && rep.toProduct?.id === product?.id));

    if (fromExists.length > 0) {
      toast.error("This product is already being replaced. Please choose a different product");

      return;
    }

    if (toExistsAsFrom.length > 0) {
      toast.error("This product is already being used as a replacement. Please choose a different product");

      return;
    }

    const newReplacements = [ ...replacements ];

    //if the index exists in the array, replace or create the input property in the replacement object
    if (newReplacements[ index ]) {
      newReplacements[ index ][ input ] = product;
    } else {
      newReplacements.push({ [ input ]: product });
    }
    const lastI = newReplacements.length - 1;

    //if the user is in the middle of choosing a replacement, hide the next replacement ui
    if (!(newReplacements[ lastI ].fromProduct && newReplacements[ lastI ].toProduct)) {
      setNewReplacementRow(false);
    }

    setReplacements(newReplacements);
  }, [ replacements ]);

  const removePlannedReplacement = React.useCallback((index: number) => {
    const newReplacements = [ ...replacements ];

    newReplacements.splice(index, 1);
    setReplacements(newReplacements);

    if (newReplacements.length === 0) {
      //back at the beginning of the flow, so add the search bar
      setNewReplacementRow(true);
    }
  }, [ replacements ]);

  //determines whether to show the add replacement button or a new search bar
  const AddOrSearchFrom = React.useMemo(() => {
    //the add new replacement button - to show when the previous replacement is complete
    const Add = (
      <div
        className="add-row-search"
        onClick={() => {
          setNewReplacementRow(true);
        }}
      >
        <div className="add-row-button">
          <Icon icon={Icons.ADD} />

          <Text>
            Add New Replacement
          </Text>
        </div>
      </div>
    );

    const NewSearch = ( //the next replacement search bar - to show when the user has clicked add new replacement
      <ProductReplacementSearch
        updateReplacementHandler={product => handleProductFormChange(replacements.length, "fromProduct", product)}
        placeholder="Search for a product to replace"
      />
    );

    const lastInReplacements = replacements[ replacements.length - 1 ];

    if (newReplacementRow) {
      //if the add replacement button has been clicked
      return NewSearch;
    } else if ((lastInReplacements?.fromProduct && lastInReplacements?.toProduct)) {
      //if last replacement is complete, show the add button
      return Add;
    } else {
      //if we're in the middle of a replacement, show nothing
      return null;
    }
  }, [
    handleProductFormChange,
    newReplacementRow,
    replacements
  ]);

  const replacementFields = React.useMemo(() => {
    const inputs = replacements.map((rep, i) => {
      return (
        <div
          className="replacement-row"
          key={rep.fromProduct?.id}
        >
          <div
            className="replacement-inputs"
          >
            <PlannedProductReplacement
              fromProduct={rep?.fromProduct}
              toProduct={rep?.toProduct}
              removeReplacementHandler={() => {
                removePlannedReplacement(i);
              }}
              updateFromProduct={product => handleProductFormChange(i, "fromProduct", product)}
              updateToProduct={product => handleProductFormChange(i, "toProduct", product)}
            />

            <hr />
          </div>
        </div>

      );
    });

    return inputs;
  }, [
    replacements,
    removePlannedReplacement,
    handleProductFormChange
  ]);

  // Remove customer and branch entities from filters to reduce the size of the data object in the request
  const mapFilters = React.useCallback((params: Partial<CreateProductReplacementParams>) => {
    const newParams = JSON.parse(JSON.stringify(params));

    for (const filter in newParams.filters) {
      if (newParams.filters[ filter ].branch) {
        delete newParams.filters[ filter ].branch;
      }

      if (newParams.filters[ filter ].customer) {
        delete newParams.filters[ filter ].customer;
      }
    }

    return newParams;
  }, [ ]);

  // handles the submit button event
  const handleSubmit = React.useCallback(() => {
    //inject replacement state into filters
    const completeReplacements = replacements.filter(rep => rep.fromProduct?.id && rep.toProduct?.id);

    const reducedReplacements: ProductReplacementId[] = completeReplacements.map(rep => {
      return {
        fromProductId: rep.fromProduct!.id,
        toProductId: rep.toProduct!.id
      };
    });

    const newForm = { ...form };

    newForm.replacements = reducedReplacements;

    createProductReplacementSchema
      .validate(newForm)
      .then(() => {
        createProductReplacementReq(mapFilters(newForm));
      })
      .catch(err => {
        setValidation({
          error: true,
          message: err.message
        });
      });
  }, [
    createProductReplacementReq,
    form,
    mapFilters,
    replacements,
    validation
  ]);

  React.useEffect(() => {
    if (createProductReplacementRes.data) {
      history.push(`/admin/products/product-replacements/${createProductReplacementRes.data.id}`);
    }

    if (createProductReplacementRes.error) {
      toast.error(createProductReplacementRes.error.message);
    }
  }, [
    createProductReplacementRes.data,
    createProductReplacementRes.error,
    history
  ]);

  // Remove a filter from the array
  const removeFilter = React.useCallback((filter: GlobalProductReplacementFilter) => {
    const newFilters = _.remove([ ...form.filters || [] ], f => {
      if (filter.customerId) {
        return f.customerId !== filter.customerId;
      }

      if (filter.branchId) {
        return (f.branchId !== filter.branchId);
      }

      return true;
    });

    handleFormChange("filters", [ ...newFilters ]);
  }, [ form.filters, handleFormChange ]);

  const filterType = React.useMemo(() => {
    if (form.type === CustomerBranchFilterDefault.AllowAll) {
      if (selectedCustomer) {
        return CustomerBranchFilterType.Include;
      } else {
        return CustomerBranchFilterType.Exclude;
      }
    } else {
      if (selectedCustomer) {
        return CustomerBranchFilterType.Exclude;
      } else {
        return CustomerBranchFilterType.Include;
      }
    }
  }, [ form.type, selectedCustomer ]);

  const customerBranchFilters = React.useMemo(() => {
    const expectedFiltersLength = props.duplicateProductReplacement?.filters?.length || 0;

    if (duplicateFilters.length === expectedFiltersLength && expectedFiltersLength > 0) {
      handleFormChange("filters", [ ...duplicateFilters ]);
      setDuplicateFilters([]);
    }

    const filtersRowsWithChildren = (
      <CustomerBranchFilters
        form={form}
        setSelectedCustomer={setSelectedCustomer}
        setShowFiltersModal={setShowFiltersModal}
        removeFilter={removeFilter}
        action={CustomerBranchFilterAction.ProductReplacement}
      />
    );

    return filtersRowsWithChildren;
  }, [
    duplicateFilters,
    form,
    handleFormChange,
    props.duplicateProductReplacement?.filters?.length,
    removeFilter
  ]);

  if (!terminology) {
    return null;
  }

  return (
    <FormContainer>
      <div className="form-inputs-top">
        {validation.error && (
          <ErrorNotice
            message={validation.message}
            type="error"
          />

        )}

        {replacements.length > 0 ? (
          <div className="to-from">
            <Text as="h4">
              From Product
            </Text>

            <Text as="h4">
              To Product
            </Text>

            <div className="new-rep-button-wrapper">
              <Icon
                icon={Icons.CLOSE}
                onClick={() => {
                  setReplacements([]);
                  setNewReplacementRow(true);
                }}
                size={20}
              />
            </div>
          </div>
        ) : null}

        {productResponse.pending ? <LoadingSpinner type="circular" /> : replacementFields}

        {AddOrSearchFrom}

        <div className="selected-dates form-row">
          <div className="form-date-input">
            <Text as="h4">
              Start Date
            </Text>

            <DateInput
              label="Start Date"
              date={form.effectOrders?.startDate ? moment.utc(form.effectOrders.startDate).toDate() : undefined}
              setDate={date => handleFormChange("effectOrders", {
                ...form.effectOrders,
                startDate: date
              })}
              minimumDate={new Date()}
            />
          </div>

          <div className="form-date-input">
            <Text as="h4">
              End Date (optional)
            </Text>

            <DateInput
              label="End Date"
              date={form.effectOrders?.endDate ? moment.utc(form.effectOrders.endDate).toDate() : undefined}
              setDate={date => handleFormChange("effectOrders", {
                ...form.effectOrders,
                endDate: date
              })}
              minimumDate={form.effectOrders?.startDate ? moment.utc(form.effectOrders.startDate).toDate() : new Date()}
              maximumDate={moment.utc().add(2, "years").toDate()}
            />
          </div>
        </div>

        <div className="form-row">
          <Text as="h4">
            Default product replacement behaviour
          </Text>

          <Text>
            By default, should this product replacement be applied to all
            {" "}

            {terminology.customer.plural.text}

            {" "}
            or applied only to the selected

            {" "}

            {terminology.customer.plural.text}

            {" "}
            and

            {" "}

            {terminology.drop.plural.text}
            ?
          </Text>

          <Radio
            options={[
              {
                text: `Allow all ${terminology.customer.plural.text} orders to be affected`,
                value: CustomerBranchFilterDefault.AllowAll,
                checked: form.type === CustomerBranchFilterDefault.AllowAll
              },
              {
                text: `Only update orders for selected ${terminology.customer.plural.text} and ${terminology.drop.plural.text}`,
                value: CustomerBranchFilterDefault.AllowNone,
                checked: form.type === CustomerBranchFilterDefault.AllowNone
              }
            ]}
            handleChange={e => {
              handleFormChange("type", e.target.value, form => {
                setForm({
                  ...form,
                  filters: []
                });
              });
            }}
            name="type"
            label=""
            value={form.type}
          />
        </div>

        <div className="form-row">
          <Text as="h4">
            Type of orders to be effected
          </Text>

          <SelectOrderTypes
            selectedOrderTypes={form.orderTypes || []}
            onChange={orderTypes => handleFormChange("orderTypes", orderTypes)}
          />
        </div>

        <div className="form-row">
          <Text as="h4">
            Should this product replacement take effect on all existing
            {" "}

            {terminology.list.plural.text}

            {" "}

            and

            {" "}

            {terminology.schedule.plural.text}

            {" "}
            containing this product
          </Text>

          {form.effectLists && (
            <Text
              color="error"
              as="h4"
            >
              {`Warning! This will affect ${terminology.list.plural.text} and any ${terminology.schedule.plural.text} attached to the ${terminology.list.plural.text}  immediately as lists are not date specific. Only use this option if you are sure you want to do this.`}
            </Text>
          )}

          {!form.orderTypes?.includes("scheduled") && (
            <Tooltip
              id="effectList"
              type="dark"
              place="right"
              getContent={() => (
                <>
                  <Text color="white">
                    You can only effect
                    {" "}

                    {terminology.list.plural.text}

                    {" "}
                    with product replacements when

                    {" "}

                    {terminology.schedule.singular.text}

                    {" "}
                    order type is selected
                  </Text>
                </>
              )}
            />
          )}

          <div
            className="effect-lists"
            data-tip
            data-for="effectList"
          >
            <CheckboxStyled
              checked={form.effectLists}
              onChange={e => handleFormChange("effectLists", e.target.checked)}
              disabled={!form.orderTypes?.includes("scheduled")}
            />

            <Text>
              Effect
              {" "}

              {terminology.list.plural.text}

              {" "}

              &

              {" "}

              {terminology.schedule.plural.text}
            </Text>
          </div>
        </div>
      </div>

      <CustomerBranchFiltersModal
        existingFilters={form.filters || []}
        isOpen={showFiltersModal}
        onDismiss={() => {
          setShowFiltersModal(false);
          setSelectedCustomer(null);
        }}
        handleSave={data => handleFormChange("filters", form.filters ? [ ...form.filters, ...data ] : data)}
        filterType={filterType}
        customer={selectedCustomer}
      />

      {customersResponse.pending || dropsResponse.pending ? <LoadingSpinner type="circular" /> : customerBranchFilters}

      <div className="submit-form">
        <Button
          loading={createProductReplacementRes.pending}
          $buttonType="primary"
          onClick={() => handleSubmit()}
        >
          Create Product Replacement
        </Button>
      </div>
    </FormContainer>
  );
};

const FormContainer = styled.div`
  ${({ theme }) => css`
    .form-inputs-top {
      max-width: 900px;
    }

    .form-row {
      margin-top: ${theme.spacing.margins.lg};
    }
    
    .add-row-button {
      display:flex;
      margin-top:${theme.spacing.margins.md};
      margin-bottom:${theme.spacing.margins.lg};
      align-items: center;
    }
    
    .add-row-button span {
      cursor: pointer;
      padding: ${theme.spacing.padding.xs};
    }

    .add-row-button svg{
      cursor: pointer;
    }
      
    .to-from{
      display: flex;
      align-items:center;
    }

    .to-from h4{
      flex:1;
      margin-bottom: 0px;
    }

    .selected-dates{
      display: flex;
    }
    
    .form-date-input {
        flex: 1;
    }

    .date-input-wrapper {
        width: 320px;
    }

    .effect-lists {
      display: flex;
      align-items: center;
      margin-bottom: ${theme.spacing.margins.lg};

      input {
        margin-right: ${theme.spacing.margins.md};
      }
    }

    .submit-form {
      margin-top: ${theme.spacing.margins.xl};
    }
    
    .new-rep-button-wrapper{
      display:flex;
      align-self: center;
      cursor: pointer; 
    }
  `}
`;

export default ProductReplacementForm;